/// <amd-module name="Test/TestSummary/Actions/TestResultsReportActionsCreator" />
import { ContributionSource, IViewContextData } from "Test/common-util/Common";
import * as TCMContracts from "Test/common-util/Contracts/Test";
import { DataProviderErrorCodes } from "Test/common-util/Contracts/ViewContracts";
import * as Telemetry from "Test/common-util/Telemetry";
import * as TelemetryConstants from "Test/common-util/TelemetryConstants";
import { TestResultsReportActionsHub } from "Test/TestSummary/Actions/TestResultsReportActionsHub";
import { TestResultSource } from "Test/TestSummary/Sources/TestResultSource";
import { IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";

export class TestResultsReportActionsCreator {

    private readonly _metricsAPIInUnifiedPipeline: string = "WebAccess.TestManagement.UnifiedPipelineMetricsAPI";
    private _pageContext: IVssPageContext;

    constructor(private _actionHub: TestResultsReportActionsHub,
        private _context: IVssComponentContext,
        private _viewContextData: IViewContextData) {
        this._testResultSource = new TestResultSource(_context);
        this._pageContext = _context.pageContext;
    }

    public initialize(artifact: IViewContextData) {
        if(this._shouldUsePipelineAPi(artifact))
        {
            this._testResultSource.getTestReportFromDataProviderForUnifiedPipelineAsync(artifact, this._context).then((reports: TCMContracts.PipelineTestMetrics) => {
                if (reports) {
                    this._invokeActionsForUnifiedPipeline(reports, artifact);
                } else {
                    this._actionHub.onSummaryLoaded.invoke({context: artifact, errorCode: DataProviderErrorCodes.NoTestResultsInScenario});
                    Telemetry.publishEvent(this._context.pageContext, this._viewContextData, TelemetryConstants.featureTestTab_ErrorInSummaryDataProvider, { "DataProviderName": "test-tab-unifiedPipeline-summary-data-provider", "reason":"No data returned from the data provider" });
                }
            }).catch((reason: any) => {
                console.error(reason);
                this._actionHub.onSummaryLoaded.invoke({context: artifact, errorMessage: reason, errorCode: DataProviderErrorCodes.ServerError});
            });
        }
        else
        {
            this._testResultSource.getTestReportFromDataProviderAsync(artifact, this._context).then((reports: TCMContracts.TestResultSummary) => {
                if (reports) {
                    this._invokeActions(reports, artifact);
                } else {
                    this._actionHub.onSummaryLoaded.invoke({context: artifact, errorCode: DataProviderErrorCodes.NoTestResultsInScenario});
                    Telemetry.publishEvent(this._context.pageContext, this._viewContextData, TelemetryConstants.featureTestTab_ErrorInSummaryDataProvider, { "DataProviderName": "test-tab-build-summary-data-provider", "reason":"No data returned from the data provider" });
                }
            }).catch((reason: any) => {
                console.error(reason);
                this._actionHub.onSummaryLoaded.invoke({context: artifact, errorMessage: reason, errorCode: DataProviderErrorCodes.ServerError});
            });
        }
    }

    public refreshTestResulstsSummary(artifact: IViewContextData) {
        if(this._shouldUsePipelineAPi(artifact))
        {
            this._testResultSource.getTestReportFromDataProviderForUnifiedPipelineAsync(artifact, this._context).then((report: TCMContracts.PipelineTestMetrics) => {
                this._invokeActionsForUnifiedPipeline(report, artifact)
            })
            .catch((error:any) => {
                console.error(error);
                this._actionHub.onSummaryLoaded.invoke({context: artifact, errorMessage: error, errorCode: DataProviderErrorCodes.ServerError});
            });
        }
        else
        {
            this._testResultSource.getTestReportFromDataProviderAsync(artifact, this._context).then((report: TCMContracts.TestResultSummary) => {
                this._invokeActions(report, artifact)
            })
            .catch((error:any) => {
                console.error(error);
                this._actionHub.onSummaryLoaded.invoke({context: artifact, errorMessage: error, errorCode: DataProviderErrorCodes.ServerError});
            });
        }
    }

    private _invokeActions(reports: TCMContracts.TestResultSummary, artifact: IViewContextData) {
        let isNoConfigRuns = false;
        if(reports.totalRunsCount > 0 && reports.noConfigRunsCount > 0 && reports.totalRunsCount === reports.noConfigRunsCount) {
            isNoConfigRuns = true;
        }
        if (this._checkForData(reports)) {
            this._actionHub.initializeTestResultSummary.invoke({ summaryData: reports, viewContextStatus: artifact.status });
            this._actionHub.onSummaryLoaded.invoke({ context: artifact, isNoConfigRuns: isNoConfigRuns });
        }
        else {
            this._actionHub.onSummaryLoaded.invoke({ context: artifact, errorCode: DataProviderErrorCodes.NoTestResultsInScenario, isNoConfigRuns: isNoConfigRuns });
        }
    }

    private _invokeActionsForUnifiedPipeline(reports: TCMContracts.PipelineTestMetrics, artifact: IViewContextData) {
        let isNoConfigRuns = false;
        if(reports.runSummary && reports.runSummary.noConfigRunsCount> 0 && reports.runSummary.noConfigRunsCount === reports.runSummary.totalRunsCount) {
            isNoConfigRuns = true;
        }
        if (reports.runSummary && reports.runSummary.totalRunsCount > 0) {
            this._actionHub.initializeTestResultSummaryForUnifiedPipeline.invoke({ summaryData: reports, viewContextStatus: artifact.status });
            this._actionHub.onSummaryLoaded.invoke({ context: artifact, isNoConfigRuns: isNoConfigRuns });
        }
        else {
            this._actionHub.onSummaryLoaded.invoke({ context: artifact, errorCode: DataProviderErrorCodes.NoTestResultsInScenario, isNoConfigRuns: isNoConfigRuns });
        }
    }

    private _checkForData(report: TCMContracts.TestResultSummary): boolean {
        const shouldShowTestReports: boolean = report.aggregatedResultsAnalysis.totalTests > 0 ||
                        this._areThereNotImpactedTests(report) ||
                        this._areThereRunsByState(TCMContracts.TestRunState.Aborted, report) ||
                        this._areThereRunsByState(TCMContracts.TestRunState.InProgress, report) ||
                        report.totalRunsCount > 0;
        return shouldShowTestReports;
    }

    private _areThereRunsByState(state: TCMContracts.TestRunState, report: TCMContracts.TestResultSummary): boolean {
        return report.aggregatedResultsAnalysis.runSummaryByState &&
            report.aggregatedResultsAnalysis.runSummaryByState[state] &&
            report.aggregatedResultsAnalysis.runSummaryByState[state].runsCount > 0;
    }
    
    private _areThereNotImpactedTests(report: TCMContracts.TestResultSummary) {
        return report.aggregatedResultsAnalysis.resultsByOutcome &&
            report.aggregatedResultsAnalysis.resultsByOutcome[TCMContracts.TestOutcome.NotImpacted] &&
            report.aggregatedResultsAnalysis.resultsByOutcome[TCMContracts.TestOutcome.NotImpacted].count > 0;
    }

    private _shouldUsePipelineAPi(artifact: IViewContextData): boolean {
        if(artifact.contributionSource === ContributionSource.Pipeline){
            return isFeatureFlagEnabled(this._pageContext, this._metricsAPIInUnifiedPipeline, false);
        }
        else {
            return false;
        }
    }


    private _testResultSource: TestResultSource;
}
